import React from "react";
import {
  Visibility,
  Edit,
  Delete,
  MoveDownOutlined,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { CustomButtonPlusIcon } from "../Design/CustomButtonPlusIcon";
import Swal from "sweetalert2";
import api from "../../../api/axios";
import TransferUsersDialog from "../TransferUsersDialog";
import { usePermissions } from "../../Context/PermissionProvider";
import { Move, MoveDown } from "lucide-react";

const TableActions = ({
  rute,
  value,
  setChangeDone = () => {},
  changeDone = false,
  showView = false,
  showEdit = false,
  showDelete = false,
  showTransfer = false,
  textTransfer,
  roleFlag = false,
  apiRute = "",
  roles = [],
  module = "",
  completeValue = [],
  userLogged = {},
}) => {
  const [openTransferModal, setOpenTransferModal] = React.useState(false);
  const [usersWithRole, setUsersWithRole] = React.useState([]);
  const [selectedRole, setSelectedRole] = React.useState("");
  const [loadingUsers, setLoadingUsers] = React.useState(false);
  const [contractDeleteModalOpen, setContractDeleteModalOpen] =
    React.useState(false);
  const [contractPreValidation, setContractPreValidation] =
    React.useState(null);
  const [otherContracts, setOtherContracts] = React.useState([]);
  const [selectedContractToReassign, setSelectedContractToReassign] =
    React.useState(null);
  const [selectedContractByType, setSelectedContractByType] =
    React.useState({});
  const [loadingContractValidation, setLoadingContractValidation] =
    React.useState(false);
  const [loadingContractAction, setLoadingContractAction] =
    React.useState(false);
  const { can } = usePermissions();

  const contractAssignmentGroups = React.useMemo(() => {
    const grouped = {};

    (contractPreValidation?.cases ?? []).forEach((caseItem) => {
      const caseTypeId = caseItem?.case_type ?? "sin-tipo";
      const caseTypeName = caseItem?.case_type_name || "Sin tipo";
      const key = String(caseTypeId);

      if (!grouped[key]) {
        grouped[key] = {
          key,
          caseTypeId: caseTypeId,
          caseTypeName,
          cases: [],
        };
      }

      grouped[key].cases.push(caseItem);
    });

    return Object.values(grouped);
  }, [contractPreValidation]);

  const hasPendingContractGroupAssignments =
    contractAssignmentGroups.length > 0
      ? contractAssignmentGroups.some(
          (group) => !selectedContractByType[group.key],
        )
      : !selectedContractToReassign;

  /* ===============================
     DELETE REAL
     =============================== */
  const confirmDelete = async (id, vista) => {
    const result = await Swal.fire({
      title: "¿Eliminar definitivamente?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: "Eliminando...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/${apiRute || vista}/destroy`,
        { id },
      );

      await Swal.fire({
        title: "Eliminado",
        icon: "success",
        timer: 1000,
        timerProgressBar: true,
        showConfirmButton: false,
        showCancelButton: false,
      });

      setChangeDone((prev) => !prev);
    } catch (error) {
      console.error("Error al eliminar el registro:", error); // Opcional, para debug
      Swal.fire(
        "Error",
        `No se pudo eliminar el registro: ${error.message || error}`,
        "error",
      );
    }
  };

  /* ===============================
     CLICK DELETE
     =============================== */
  const handleDelete = async (id, vista) => {
    const isContractDelete =
      apiRute === "contracts" || module === "contracts" || rute === "contratos";

    if (isContractDelete) {
      setContractPreValidation(null);
      setOtherContracts([]);
      setSelectedContractToReassign(null);
      setSelectedContractByType({});
      setLoadingContractValidation(true);

      try {
        Swal.fire({
          title: "Comprobando expedientes asociados...",
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
          showConfirmButton: false,
          showCancelButton: false,
        });

        const { data } = await api.post(
          `${process.env.REACT_APP_API_URL}/contracts/pre-validate-delete`,
          { id },
        );

        Swal.close();

        if ((data?.affectedCases ?? 0) === 0) {
          await confirmDelete(id, vista);
          return;
        }

        setContractPreValidation({ ...data, contractId: id });
        setOtherContracts(data.otherContracts || []);
        setContractDeleteModalOpen(true);
      } catch (error) {
        console.error("Error al validar contrato antes de eliminar:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.response?.data?.message ||
            "No se pudo validar la eliminación del contrato.",
        });
      } finally {
        setLoadingContractValidation(false);
      }

      return;
    }

    if (!roleFlag) return confirmDelete(id, vista);

    try {
      setUsersWithRole([]);
      setSelectedRole("");
      setLoadingUsers(true);
      setOpenTransferModal(true);

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/users/search`,
        { type: id, roleflag: true },
      );

      if (data?.type?.length > 0) {
        setUsersWithRole(data.type);
      } else {
        setOpenTransferModal(false);

        await Swal.fire({
          title: "Sin usuarios asociados",
          text: "Continuando con la eliminación…",
          timer: 2500,
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        confirmDelete(id, vista);
      }
    } catch {
      Swal.fire("Error", "No se pudo verificar usuarios.", "error");
      setOpenTransferModal(false);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleReassignContractAndDelete = async () => {
    if (!contractPreValidation || contractPreValidation.affectedCases === 0) {
      await confirmDelete(contractPreValidation?.contractId || value, rute);
      setContractDeleteModalOpen(false);
      return;
    }

    const reassignmentPayload = {};

    if (contractAssignmentGroups.length > 0) {
      const missingAssignments = contractAssignmentGroups.filter(
        (group) => !selectedContractByType[group.key],
      );

      if (missingAssignments.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Reasignación requerida",
          text: "Debe seleccionar un contrato para cada tipo de caso antes de eliminar este contrato.",
        });
        return;
      }

      reassignmentPayload.reassignments = contractAssignmentGroups.map(
        (group) => ({
          case_type: group.caseTypeId === "sin-tipo" ? null : group.caseTypeId,
          newContractId: selectedContractByType[group.key]?.id,
        }),
      );
    } else {
      if (!selectedContractToReassign) {
        Swal.fire({
          icon: "warning",
          title: "Reasignación requerida",
          text: "Debe seleccionar un contrato nuevo para reasignar los expedientes antes de eliminar este contrato.",
        });
        return;
      }

      reassignmentPayload.newContractId = selectedContractToReassign.id;
    }

    setLoadingContractAction(true);

    try {
      const beforeCases = contractPreValidation?.cases ?? [];
      const reassignmentResponse = await api.post(
        `${process.env.REACT_APP_API_URL}/contracts/reassign-before-delete`,
        {
          id: contractPreValidation.contractId,
          ...reassignmentPayload,
        },
      );

      const updatedCases = reassignmentResponse?.data?.updatedCases ?? [];
      const beforeComparison = beforeCases.map((caseItem) => ({
        expedienteId: caseItem?.id ?? "N/A",
        before:
          Number(caseItem?.case_contract) ===
          Number(contractPreValidation.contractId),
        after:
          updatedCases.some(
            (item) =>
              Number(item.id) === Number(caseItem?.id) &&
              Number(item.case_contract) !== Number(contractPreValidation.contractId),
          ),
      }));

      console.log("[CONTRACT REASSIGN DEBUG] Comparación final:", beforeComparison);

      await api.post(`${process.env.REACT_APP_API_URL}/contracts/destroy`, {
        id: contractPreValidation.contractId,
      });

      Swal.fire({
        icon: "success",
        title: "Contrato eliminado",
        text: `Se realizarán cambios en ${contractPreValidation?.affectedCases ?? 0} expediente(s) y el contrato ha sido eliminado.`,
      });

      setContractDeleteModalOpen(false);
      setSelectedContractByType({});
      setSelectedContractToReassign(null);
      setChangeDone((prev) => !prev);
    } catch (error) {
      console.error("Error al reasignar y eliminar contrato:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "No se pudo completar la reasignación y eliminación.",
      });
    } finally {
      setLoadingContractAction(false);
    }
  };

  const handleDeleteContractAnyway = async () => {
    if (!contractPreValidation) return;

    const confirm = await Swal.fire({
      title: "Continuar y eliminar de todos modos",
      text: "Esta acción dejará todos los expedientes asociados sin contrato y eliminará el contrato actual.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Continuar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    setLoadingContractAction(true);

    try {
      await api.post(`${process.env.REACT_APP_API_URL}/contracts/destroy`, {
        id: contractPreValidation.contractId,
        clear_cases: true,
      });

      Swal.fire({
        icon: "success",
        title: "Contrato eliminado",
        text: `Se realizarán cambios en ${contractPreValidation?.affectedCases ?? 0} expediente(s) y el contrato ha sido eliminado.`,
      });

      setContractDeleteModalOpen(false);
      setChangeDone((prev) => !prev);
    } catch (error) {
      console.error("Error al eliminar contrato forzando limpieza:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "No se pudo completar la eliminación forzada del contrato.",
      });
    } finally {
      setLoadingContractAction(false);
    }
  };

  /* ===============================
     ASIGNAR NUEVO ROL
     =============================== */
  const handleAssignNewRole = async () => {
    const confirm = await Swal.fire({
      title: "¿Asignar nuevo rol?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Asignar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    try {
      Swal.fire({
        title: "Actualizando usuarios...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await api.post(`${process.env.REACT_APP_API_URL}/roles/newassignment`, {
        list: usersWithRole.map((u) => ({ id: u.id })),
        newrole: selectedRole,
      });

      Swal.fire("Usuarios actualizados", "", "success");
      setOpenTransferModal(false);

      confirmDelete(value, rute);
    } catch (error) {
      console.log(error);
      Swal.fire("Error", "No se pudo completar la transferencia.", "error");
    }
  };

  if (!module) return true;

  return showView || showEdit || showDelete ? (
    <>
      <div
        className="spanClassForTables centerContent"
        style={{ gap: 8, display: "flex", justifyContent: "right" }}>
        {userLogged?.type == 1 &&
          completeValue?.company_id == 1 &&
          showTransfer &&
          rute === "clientes" && (
            <div style={{ width: "132px" }}>
              <CustomButtonPlusIcon
                icon={MoveDownOutlined}
                to={`/${textTransfer}/transferir?id=${value}`}
                text={`Transferir`}
                size="small"
                position="start"
                color="secondary"
                variant="outlined"
              />
            </div>
          )}
        {showView && can(module, "view") && (
          <CustomButtonPlusIcon
            icon={Visibility}
            to={`/${rute}/ver?id=${value}`}
            size="small"
            color="success"
            variant="outlined"
          />
        )}
        {showEdit && can(module, "edit") && (
          <CustomButtonPlusIcon
            icon={Edit}
            to={`/${rute}/editar?id=${value}`}
            size="small"
            color="info"
            variant="outlined"
          />
        )}

        {showDelete && can(module, "delete") && (
          <CustomButtonPlusIcon
            icon={Delete}
            handleFunction={() => handleDelete(value, rute)}
            size="small"
            color="error"
            variant="outlined"
          />
        )}
      </div>

      <TransferUsersDialog
        open={openTransferModal}
        onClose={() => setOpenTransferModal(false)}
        loading={loadingUsers}
        users={usersWithRole}
        roles={roles}
        selectedRole={selectedRole}
        onRoleChange={setSelectedRole}
        onConfirm={handleAssignNewRole}
        currentRoleId={value}
      />

      <Dialog
        open={contractDeleteModalOpen}
        onClose={() => setContractDeleteModalOpen(false)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>Contrato asociado a expedientes</DialogTitle>
        <DialogContent dividers>
          {loadingContractValidation ? (
            <Typography variant="body2">
              Comprobando expedientes asociados…
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Si desea eliminarlo, primero debe reasignar esos expedientes a
                un contrato nuevo.
              </Typography>

              {contractAssignmentGroups.length > 0 ? (
                <div
                  style={{
                    display: "grid",
                    gap: 16,
                  }}>
                  {contractAssignmentGroups.map((group) => (
                    <div
                      key={group.key}
                      style={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 12,
                        padding: 12,
                        background: "#fafafa",
                      }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {group.caseTypeName}
                      </Typography>

                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          marginBottom: 12,
                        }}>
                        {group.cases.map((caseItem) => (
                          <span
                            key={caseItem.id}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "4px 8px",
                              borderRadius: 999,
                              background: "#eef2ff",
                              fontSize: 12,
                              color: "#374151",
                            }}>
                            Exp. #{caseItem.id}
                          </span>
                        ))}
                      </div>

                      <Autocomplete
                        size="small"
                        options={otherContracts}
                        getOptionLabel={(option) => option?.contract_title || ""}
                        value={selectedContractByType[group.key] ?? null}
                        onChange={(_, newValue) =>
                          setSelectedContractByType((prev) => ({
                            ...prev,
                            [group.key]: newValue,
                          }))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`Contrato para ${group.caseTypeName}`}
                            placeholder="Selecciona un contrato"
                            fullWidth
                          />
                        )}
                        noOptionsText="No hay contratos disponibles"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <Autocomplete
                  size="small"
                  options={otherContracts}
                  getOptionLabel={(option) => option?.contract_title || ""}
                  value={selectedContractToReassign}
                  onChange={(_, newValue) =>
                    setSelectedContractToReassign(newValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contrato de destino"
                      placeholder="Selecciona un contrato"
                      fullWidth
                    />
                  )}
                  noOptionsText="No hay contratos disponibles"
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            pt: 2,
          }}>
          <Button
            onClick={() => setContractDeleteModalOpen(false)}
            disabled={loadingContractAction}
            sx={{ minWidth: 120 }}>
            Cancelar
          </Button>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}>
            <Button
              onClick={handleDeleteContractAnyway}
              variant="outlined"
              color="error"
              disabled={
                loadingContractAction ||
                loadingContractValidation ||
                !contractPreValidation ||
                contractPreValidation.affectedCases === 0
              }
              sx={{ whiteSpace: "nowrap" }}>
              {loadingContractAction ? "Procesando..." : "Continuar y eliminar"}
            </Button>
            <Button
              onClick={handleReassignContractAndDelete}
              variant="contained"
              color="error"
              disabled={
                loadingContractAction ||
                loadingContractValidation ||
                !contractPreValidation ||
                contractPreValidation.affectedCases === 0 ||
                hasPendingContractGroupAssignments
              }
              sx={{ whiteSpace: "nowrap" }}>
              {loadingContractAction ? "Procesando..." : "Reasignar y eliminar"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  ) : null;
};

export default TableActions;
